<template>
  <div class="assets-grid-card">
    <div class="assets-grid-card__header">
      <div class="assets-grid-title">
        <div class="assets-grid-title__icon">
          <i class="bi bi-terminal" />
        </div>
        <div>
          <div class="assets-grid-title__text">
            {{ $t('components.infrastructure.console.title') }}
          </div>
        </div>
      </div>
      <span
        class="wirepas-status-badge"
        :class="connected ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
      >
        <span
          class="wirepas-status-dot"
          :class="connected ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
        />
        {{ connected ? $t('components.infrastructure.console.brokerOnline') : $t('components.infrastructure.console.brokerOffline') }}
      </span>
    </div>

    <div class="assets-grid-card__body">
      <div class="wl-console-subtabs">
        <button
          v-for="v in views"
          :key="v"
          type="button"
          class="tab-link"
          :class="{ active: view === v }"
          @click="view = v"
        >
          {{ $t(`components.infrastructure.console.views.${v}`) }}
        </button>
      </div>

      <div
        v-if="error"
        class="alert alert-danger d-flex align-items-center gap-2 mt-3 mx-3"
      >
        <i class="bi bi-exclamation-triangle-fill" />
        <span>{{ error }}</span>
      </div>

      <div
        v-if="hasActivated"
        class="wp-grid-wrap"
      >
        <div
          v-if="loading"
          class="wp-loading wp-loading--overlay"
        >
          <span
            class="spinner-border wp-loading__spinner"
            role="status"
          />
        </div>

        <!-- Wiadomości -->
        <template v-if="view === 'messages'">
          <div class="wl-assets-search-wrap">
            <input
              v-model="messageSearch"
              type="text"
              class="form-control wl-assets-search"
              :placeholder="$t('components.infrastructure.console.searchMessages')"
            >
          </div>
          <div
            v-if="!filteredMessages.length"
            class="state-msg text-muted p-4 text-center"
          >
            {{ $t('components.infrastructure.console.empty') }}
          </div>
          <div
            v-else
            class="wl-console-messages"
          >
            <div
              v-for="(m, i) in filteredMessages"
              :key="i"
              class="wl-console-message"
            >
              <div class="wl-console-message__chips">
                <span class="wl-chip wl-chip--muted">{{ formatTs(m.ts) }}</span>
                <span class="wl-chip">{{ shortGwId(m.gw_id) }} / {{ m.sink_id }}</span>
                <span class="wl-chip wl-chip--danger">{{ m.src_addr }}</span>
                <span class="wl-chip wl-chip--warning">{{ m.src_ep }} → {{ m.dst_ep }}</span>
                <span
                  v-if="m.hops != null || m.travel_ms != null"
                  class="wl-chip wl-chip--info"
                >{{ m.hops != null ? `${m.hops} hop(s)` : '' }}{{ m.travel_ms != null ? ` ${m.travel_ms} ms` : '' }}</span>
                <span class="wl-chip wl-chip--success">{{ m.size }} B</span>
              </div>
              <div class="wl-console-message__hex">{{ formatHex(m.hex) }}</div>
            </div>
          </div>
        </template>

        <!-- Węzły -->
        <template v-else-if="view === 'nodes'">
          <div class="wl-assets-search-wrap">
            <input
              v-model="nodeSearch"
              type="text"
              class="form-control wl-assets-search"
              :placeholder="$t('components.infrastructure.console.searchNodes')"
            >
          </div>
          <DxDataGrid
            class="ni-grid"
            :data-source="filteredNodes"
            key-expr="address"
            :hover-state-enabled="true"
            :column-auto-width="true"
            :word-wrap-enabled="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :no-data-text="$t('components.infrastructure.console.empty')"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="15" />
            <DxPager
              :show-info="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="address"
              :caption="$t('components.infrastructure.console.columns.address')"
              :min-width="130"
              cell-template="addr-template"
            />
            <DxColumn
              data-field="lastTs"
              :caption="$t('components.infrastructure.console.columns.lastSeen')"
              :min-width="150"
              sort-order="desc"
              cell-template="lastts-template"
            />
            <DxColumn
              data-field="gw_id"
              :caption="$t('components.infrastructure.console.columns.gateway')"
              :min-width="180"
              cell-template="gwid-template"
            />
            <DxColumn
              data-field="sink_id"
              :caption="$t('components.infrastructure.console.columns.sink')"
              :min-width="90"
            />
            <DxColumn
              data-field="count"
              :caption="$t('components.infrastructure.console.columns.count')"
              :min-width="100"
              alignment="center"
            />

            <template #addr-template="{ data }">
              <span class="font-monospace small">{{ data.data.address }}</span>
            </template>
            <template #lastts-template="{ data }">
              <span class="text-muted small font-monospace">{{ agoText(data.data.lastTs) }}</span>
            </template>
            <template #gwid-template="{ data }">
              <span class="text-muted small font-monospace">{{ shortGwId(data.data.gw_id) }}</span>
            </template>
          </DxDataGrid>
        </template>

        <!-- Bramy -->
        <template v-else-if="view === 'gateways'">
          <DxDataGrid
            class="ni-grid"
            :data-source="gateways"
            key-expr="gw_id"
            :hover-state-enabled="true"
            :column-auto-width="true"
            :word-wrap-enabled="false"
            :show-borders="false"
            :show-column-lines="false"
            :show-row-lines="false"
            :row-alternation-enabled="true"
            :no-data-text="$t('components.infrastructure.console.empty')"
          >
            <DxLoadPanel :enabled="false" />
            <DxPaging :page-size="15" />
            <DxPager
              :show-info="true"
              :show-navigation-buttons="true"
            />

            <DxColumn
              data-field="gw_id"
              :caption="$t('components.infrastructure.console.columns.gateway')"
              :min-width="190"
              cell-template="cgwid-template"
            />
            <DxColumn
              data-field="model"
              :caption="$t('components.infrastructure.console.columns.model')"
              :min-width="150"
            />
            <DxColumn
              data-field="version"
              :caption="$t('components.infrastructure.console.columns.version')"
              :min-width="110"
            />
            <DxColumn
              data-field="sinkCount"
              :caption="$t('components.infrastructure.console.columns.sinks')"
              :min-width="90"
              alignment="center"
            />
            <DxColumn
              data-field="online"
              :caption="$t('components.infrastructure.console.columns.status')"
              :min-width="120"
              alignment="center"
              cell-template="online-template"
            />

            <template #cgwid-template="{ data }">
              <span class="text-muted small font-monospace">{{ data.data.gw_id }}</span>
            </template>
            <template #online-template="{ data }">
              <span
                class="wirepas-status-badge"
                :class="data.data.online ? 'wirepas-status-badge--online' : 'wirepas-status-badge--offline'"
              >
                <span
                  class="wirepas-status-dot"
                  :class="data.data.online ? 'wirepas-status-dot--online' : 'wirepas-status-dot--offline'"
                />
                {{ data.data.online ? $t('components.infrastructure.online') : $t('components.infrastructure.offline') }}
              </span>
            </template>
          </DxDataGrid>
        </template>

        <!-- Częstotliwość -->
        <template v-else>
          <div class="wl-console-cadence-controls">
            <button
              v-for="w in cadenceWindows"
              :key="w.sec"
              type="button"
              class="tab-link"
              :class="{ active: cadenceWindowSec === w.sec }"
              @click="cadenceWindowSec = w.sec"
            >
              {{ w.label }}
            </button>
            <select
              v-model="cadenceAddr"
              class="form-select form-select-sm wl-console-cadence-select"
            >
              <option value="">
                {{ $t('components.infrastructure.console.allAddresses') }}
              </option>
              <option
                v-for="a in cadence.addrs"
                :key="a"
                :value="a"
              >
                {{ a }}
              </option>
            </select>
          </div>
          <div class="table-scroll">
            <table class="table wl-console-table">
              <thead>
                <tr>
                  <th>{{ $t('components.infrastructure.console.columns.endpoint') }}</th>
                  <th>{{ $t('components.infrastructure.console.columns.packetType') }}</th>
                  <th class="text-end">
                    {{ $t('components.infrastructure.console.columns.packets') }}
                  </th>
                  <th class="text-end">
                    {{ $t('components.infrastructure.console.columns.avgInterval') }}
                  </th>
                  <th class="text-end">
                    {{ $t('components.infrastructure.console.columns.perMinute') }}
                  </th>
                  <th class="text-end">
                    {{ $t('components.infrastructure.console.columns.lastPacket') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="e in cadence.endpoints"
                  :key="e.ep"
                >
                  <td class="font-monospace">
                    {{ e.ep }}
                  </td>
                  <td>{{ e.label }}</td>
                  <td class="text-end font-monospace">
                    {{ e.count }}
                  </td>
                  <td class="text-end font-monospace">
                    {{ formatDur(e.avgIntervalMs) }}
                  </td>
                  <td class="text-end font-monospace">
                    {{ e.ratePerMin }}
                  </td>
                  <td class="text-end font-monospace text-muted">
                    {{ formatDur(e.lastAgoMs) }}
                  </td>
                </tr>
                <tr v-if="!cadence.endpoints.length">
                  <td
                    colspan="6"
                    class="text-center text-muted p-4"
                  >
                    {{ $t('components.infrastructure.console.empty') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  DxDataGrid,
  DxColumn,
  DxPaging,
  DxPager,
  DxLoadPanel,
} from 'devextreme-vue/data-grid'
import {
  getWirepasConsoleMessages,
  getWirepasConsoleNodes,
  getWirepasConsoleGateways,
  getWirepasConsoleCadence,
} from '@/crud/wirepas-location'
import { wirepasAgoText, shortGwId as shortGwIdUtil, wirepasFmtTs, wirepasFmtHex, wirepasShortDurText } from '@/utils/wirepas'
import { usePolling } from '@/composables/usePolling'

type ConsoleView = 'messages' | 'nodes' | 'gateways' | 'cadence'

// Same cadence Topology uses for its own "NA ŻYWO" view.
const POLL_MS = 30000

export default defineComponent({
  name: 'WirepasMqttConsoleGrid',
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxLoadPanel },
  props: {
    active: { type: Boolean as PropType<boolean>, default: false },
  },
  setup(props) {
    const { t } = useI18n()

    const views: ConsoleView[] = ['messages', 'nodes', 'gateways', 'cadence']
    const view = ref<ConsoleView>('messages')

    const error = ref<string | null>(null)
    const loading = ref(false)
    const connected = ref(false)

    const messages = ref<any[]>([])
    const messageSearch = ref('')
    const nodes = ref<any[]>([])
    const nodeSearch = ref('')
    const gateways = ref<any[]>([])
    const cadence = ref<{ endpoints: any[]; addrs: string[] }>({ endpoints: [], addrs: [] })
    const cadenceWindowSec = ref(900)
    const cadenceAddr = ref('')
    const cadenceWindows = [
      { sec: 300, label: '5 min' },
      { sec: 900, label: '15 min' },
      { sec: 1800, label: '30 min' },
    ]

    const fetchActiveView = async () => {
      loading.value = true
      try {
        if (view.value === 'messages') {
          const res = await getWirepasConsoleMessages(200)
          connected.value = !!res.data?.connected
          messages.value = res.data?.messages ?? []
        } else if (view.value === 'nodes') {
          const res = await getWirepasConsoleNodes()
          connected.value = !!res.data?.connected
          nodes.value = res.data?.nodes ?? []
        } else if (view.value === 'gateways') {
          const res = await getWirepasConsoleGateways()
          connected.value = !!res.data?.connected
          gateways.value = res.data?.gateways ?? []
        } else {
          const res = await getWirepasConsoleCadence(cadenceWindowSec.value, cadenceAddr.value || undefined)
          connected.value = !!res.data?.connected
          cadence.value = { endpoints: res.data?.endpoints ?? [], addrs: res.data?.addrs ?? [] }
        }
        error.value = null
      } catch (e: any) {
        error.value = e?.response?.data?.error || e?.message || t('components.infrastructure.error')
      } finally {
        loading.value = false
      }
    }

    const polling = usePolling(fetchActiveView, POLL_MS)

    // Grid renders only after first activation, matching every other Infrastructure tab.
    const hasActivated = ref(props.active)
    watch(() => props.active, (isActive) => {
      if (isActive) {
        hasActivated.value = true
        fetchActiveView()
        polling.start()
      } else {
        polling.stop()
      }
    }, { immediate: true })

    // Fetch immediately on sub-view/window/address change instead of waiting for the next poll.
    watch([view, cadenceWindowSec, cadenceAddr], () => {
      if (props.active) fetchActiveView()
    })

    const filteredMessages = computed(() => {
      const q = messageSearch.value.toLowerCase().trim()
      if (!q) return messages.value
      return messages.value.filter((m) => [m.gw_id, m.sink_id, m.network, m.src_addr, m.src_ep, m.dst_ep, m.hex]
        .join(' ').toLowerCase().includes(q))
    })

    const filteredNodes = computed(() => {
      const q = nodeSearch.value.toLowerCase().trim()
      if (!q) return nodes.value
      return nodes.value.filter((n) => [n.address, n.gw_id, n.sink_id].join(' ').toLowerCase().includes(q))
    })

    const shortGwId = shortGwIdUtil
    const agoText = (tsMs: number | null) => wirepasAgoText(tsMs ? Math.floor(tsMs / 1000) : null, t)
    const formatTs = wirepasFmtTs
    const formatHex = wirepasFmtHex
    const formatDur = wirepasShortDurText

    return {
      views,
      view,
      error,
      loading,
      connected,
      hasActivated,
      messages,
      messageSearch,
      filteredMessages,
      nodes,
      nodeSearch,
      filteredNodes,
      gateways,
      cadence,
      cadenceWindowSec,
      cadenceAddr,
      cadenceWindows,
      shortGwId,
      agoText,
      formatTs,
      formatHex,
      formatDur,
    }
  },
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/wirepas-locations-view.scss';
@import '@/assets/scss/wirepas-mqtt-console-view.scss';
</style>
